import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  HomeOutlined,
  FileOutlined,
  ShoppingOutlined,
  DollarOutlined,
  ClockCircleOutlined,
  UserOutlined
} from '@ant-design/icons';
import { useAuthStore } from '../stores/authStore';

// 权限检查函数
const checkUserPermission = (user) => {
  // 管理员始终有权限
  if (user?.role === 'admin') {
    return true;
  }

  // 检查是否是付费会员
  const paidMembers = ['VIP会员', '高级会员', '银卡会员', '金卡会员', '钻石会员'];
  if (paidMembers.includes(user?.member_level)) {
    return true;
  }

  // 检查试用期
  if (user?.trial_end_date) {
    const trialEndDate = new Date(user.trial_end_date);
    const now = new Date();
    return now <= trialEndDate;
  }

  return false;
};

// 主导航项（移动端底栏与桌面侧边栏共用同一份数据）
const NAV_ITEMS = [
  { key: 'home', label: '首页', path: '/', icon: <HomeOutlined /> },
  { key: 'files', label: '文件', path: '/files', icon: <FileOutlined /> },
  { key: 'items', label: '物品', path: '/items', icon: <ShoppingOutlined /> },
  { key: 'finance', label: '资金', path: '/finance', icon: <DollarOutlined /> },
  { key: 'time', label: '时间', path: '/time', icon: <ClockCircleOutlined /> },
  { key: 'profile', label: '我的', path: '/profile', icon: <UserOutlined /> },
];

// 桌面/移动断点
const DESKTOP_BREAKPOINT = 1024;

const useIsDesktop = () => {
  const [isDesktop, setIsDesktop] = useState(
    typeof window !== 'undefined' ? window.innerWidth >= DESKTOP_BREAKPOINT : false
  );
  useEffect(() => {
    const onResize = () => setIsDesktop(window.innerWidth >= DESKTOP_BREAKPOINT);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return isDesktop;
};

const Layout = ({ children }) => {
  const { user } = useAuthStore();
  const location = useLocation();
  const navigate = useNavigate();
  const isDesktop = useIsDesktop();
  const [activeTab, setActiveTab] = useState('home');

  // 根据当前路径设置活动标签（用于底栏/侧边栏高亮）
  useEffect(() => {
    const path = location.pathname;
    const matched = NAV_ITEMS.find((it) => it.path === path);
    setActiveTab(matched ? matched.key : (path === '/' ? 'home' : ''));
  }, [location.pathname]);

  // 处理标签切换（导航 + 权限逻辑保持不变）
  const handleTabChange = (key) => {
    setActiveTab(key);

    // 预留：会员权限判断（与原逻辑一致，主导航项均为基础/公共页）
    checkUserPermission(user);

    switch (key) {
      case 'home':
        navigate('/');
        break;
      case 'files':
        navigate('/files');
        break;
      case 'items':
        navigate('/items');
        break;
      case 'finance':
        navigate('/finance');
        break;
      case 'time':
        navigate('/time');
        break;
      case 'profile':
        // 直接导航到profile页面，让App.js处理登录检查
        navigate('/profile');
        break;
      case 'card-sales':
        navigate('/card-sales');
        break;
      case 'my-orders':
        navigate('/my-orders');
        break;
      default:
        navigate('/');
        break;
    }
  };

  // ===== 桌面版：左侧边栏 + 宽内容区 =====
  if (isDesktop) {
    return (
      <div className="app-desktop">
        <aside className="side-nav">
          <div className="side-brand" onClick={() => handleTabChange('home')}>
            <span className="side-brand-mark">管</span>
            <span className="side-brand-name">管理师</span>
          </div>
          <nav className="side-nav-list">
            {NAV_ITEMS.map((it) => (
              <div
                key={it.key}
                className={`side-nav-item ${activeTab === it.key ? 'active' : ''}`}
                onClick={() => handleTabChange(it.key)}
              >
                <span className="side-nav-icon">{it.icon}</span>
                <span className="side-nav-label">{it.label}</span>
              </div>
            ))}
          </nav>
          <div className="side-nav-footer">综合管理 · 让生活井井有条</div>
        </aside>
        <main className="desktop-content">
          {children}
        </main>
      </div>
    );
  }

  // ===== 移动版：内容区 + 悬浮底部导航 =====
  return (
    <div className="app-container" style={{
      display: 'flex',
      flexDirection: 'column',
      minHeight: '100vh',
      background: '#f5f5f5'
    }}>
      {/* 主内容区域 */}
      <div className="main-content" style={{
        flex: 1,
        overflow: 'auto',
        paddingBottom: 'calc(88px + env(safe-area-inset-bottom, 0px))' // 为悬浮底栏留出空间
      }}>
        {children}
      </div>

      {/* 底部导航栏 - 原生 App 悬浮胶囊风格 */}
      <div className="bottom-nav">
        <div className="nav-items">
          {NAV_ITEMS.map((it) => (
            <div
              key={it.key}
              className={`nav-item ${activeTab === it.key ? 'active' : ''}`}
              onClick={() => handleTabChange(it.key)}
            >
              <span className="nav-icon">{it.icon}</span>
              <span className="nav-text">{it.label}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Layout;
